import { isNil } from 'ramda'

import {
  ReportAggregation,
  ReportFilter,
  ReportFilterOperation,
  ReportMetric,
  ReportMetricType,
  ReportQuery,
  ReportSearchSort,
  ReportValue,
} from '@driftt/api-types'

import { isNilOrEmpty } from 'utils/ramda'

import { createFilter, createMetric, createSort } from './creators'
import { ISortConfig } from './types'

export default abstract class QueryBuilder implements ReportQuery {
  public size?: number
  public offset?: number
  public metrics: ReportMetric[]
  public aggregations: ReportAggregation[]
  public filters: ReportFilter[]
  public sorts: ReportSearchSort[]
  public filterGroups: any[]

  constructor() {
    this.aggregations = []
    this.sorts = []
    this.metrics = []
    this.filters = []
    this.filterGroups = []
  }

  public withDocCount() {
    this.metrics.push({ type: 'DOC_COUNT' })
    return this
  }

  public withAggregation(aggregation: ReportAggregation) {
    this.aggregations.push(aggregation)
    return this
  }

  public withSort(sortConfig?: ISortConfig) {
    if (sortConfig) {
      const { metric, order } = sortConfig
      const sort = createSort(metric, order)
      if (sortConfig && Array.isArray(sortConfig)) {
        this.sorts = this.sorts.concat(sortConfig)
      } else {
        this.sorts.push(sort)
      }
    }
    return this
  }

  public withFilter(
    property: string,
    operation: ReportFilterOperation,
    value: ReportValue | ReportValue[] | null = null
  ) {
    this.filters.push(createFilter(property, operation, value))
    return this
  }

  public withMetric(
    property: string,
    type: ReportMetricType,
    metricType?: ReportMetricType,
    options = null
  ) {
    this.metrics.push(createMetric(property, type, metricType, options))
    return this
  }

  public withFilterGroups(q: any) {
    if (q.compoundQuery && !isNilOrEmpty(q.compoundQuery)) {
      this.filterGroups.push({
        negateFilterGroup: false,
        shouldMatch: q.compoundQuery.length,
        filters: q.compoundQuery,
      })
    }

    return this
  }

  public withFilters(q: any) {
    if (q.botStatus && !!q.botStatus) {
      this.withFilterType('botStatus', q.botStatus)
    }
    if (q.workspaceAgents && !isNilOrEmpty(q.workspaceAgents)) {
      this.withFilterType('agentsSelected', q.workspaceAgents)
    }
    if (q.teamSelected && !isNilOrEmpty(q.teamSelected)) {
      this.withFilterType('agentsSelected', q.teamSelected.members)
    }
    if (q.agentsSelected && !isNilOrEmpty(q.agentsSelected)) {
      this.withFilterType(
        'agentsSelected',
        q.agentsSelected.map((agent) => agent.value)
      )
    }
    if (q.widgetStatus && !isNil(q.widgetStatus)) {
      this.withFilterType('widgetStatus', q.widgetStatus)
    }
    if (q.inboxSelected && !!q.inboxSelected) {
      this.withFilterType('inboxSelected', q.inboxSelected)
    }
    if (q.classificationId && !!q.classificationId && !q.metrics) {
      this.withFilterType('classificationId', q.classificationId)
    }

    return this
  }

  public abstract withFilterType(filterType: string, value: ReportValue)

  public build(): ReportQuery {
    const {
      aggregations,
      sorts,
      metrics,
      filters,
      filterGroups,
      size,
      offset,
    } = this
    const query: ReportQuery = {}

    if (aggregations.length > 0) {
      query.aggregations = aggregations
    }
    if (sorts.length > 0) {
      query.sorts = sorts
    }
    if (filters.length > 0) {
      query.filters = filters
    }
    if (filterGroups.length > 0) {
      query.filterGroups = filterGroups
    }
    if (metrics.length > 0) {
      query.metrics = metrics
    }
    if (!isNil(size)) {
      query.size = size
    }
    if (!isNil(offset)) {
      query.offset = offset
    }

    return query
  }
}
